import { useNavigate, useSearch } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { useFormat } from '@nucleus-commerce/i18n/react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { session } from '@/shared/session/session';

import { SchedulesNav } from '../components/schedules-nav';
import { UpcomingList } from '../components/upcoming-list';
import { UPCOMING_DAYS, useScheduleTimeline } from '../hooks/use-schedule-timeline';

const ROUTE_ID = '/_authed/_active/org/schedules/upcoming' as const;
const ROUTE_PATH = '/org/schedules/upcoming' as const;
const ALL = '__all__';

/**
 * Everything due in the next four weeks across the organisation: rotation runs of active
 * schedules and one-shot publish / unpublish dates, day by day, optionally for one store.
 */
function SchedulesUpcoming() {
  const { t } = useTranslation('schedules');
  const format = useFormat();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const { me } = session.useSession();
  const stores = useMemo(() => me?.stores ?? [], [me?.stores]);
  const storeNames = useMemo(() => new Map(stores.map((s) => [s.id, s.name])), [stores]);
  const storeId = search.storeId ?? null;
  const timeline = useScheduleTimeline({ storeId });

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('upcoming.intro', { days: UPCOMING_DAYS, zone: format.timeZone })}
        tour="schedules.upcoming.header"
      />
      <SchedulesNav />
      <div className="mb-4 flex flex-wrap gap-2">
        <Select
          value={storeId ?? ALL}
          onValueChange={(value) =>
            void navigate({ search: { storeId: value === ALL ? undefined : value }, replace: true })
          }
        >
          <SelectTrigger className="w-60" aria-label={t('filters.store')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('runs.allStores')}</SelectItem>
            {stores.map((store) => (
              <SelectItem key={store.id} value={store.id}>
                {store.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {timeline.isError ? (
        <ErrorState error={timeline.error} onRetry={() => void timeline.refetch()} />
      ) : timeline.isPending ? (
        <div className="flex flex-col gap-2" aria-busy>
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
      ) : (
        <UpcomingList timeline={timeline.data} storeNames={storeNames} format={format} />
      )}
      <p className="text-muted-foreground mt-4 text-xs">{t('upcoming.endingNote')}</p>
    </PageContent>
  );
}

export function SchedulesUpcomingPage() {
  return (
    <FeatureErrorBoundary name="schedules.upcoming">
      <SchedulesUpcoming />
    </FeatureErrorBoundary>
  );
}
